import React, { useEffect, useState, useMemo } from 'react';
import { Grid, Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { GLYPHS, StatTile } from '@/components/ui';
import { useAccountBookStore } from '@/store/accountBookStore';
import { useResponsive } from '@/hooks/useResponsive';
import { addDate, getStartOfMonth, getEndOfMonth } from '@/utils/dateUtils';
import { useEmployeeData, useWorkScheduleData, useEmployeePayroll } from '@/hooks';
import {
  isDailyWorkerPay,
  isBusinessServicePay,
  isRegularEmployee,
  resolvePayType,
} from '@/types/employee';
import DailyWorkerInsuranceAdvisorCard from './DailyWorkerInsuranceAdvisorCard';
import MonthlySummaryTable from './MonthlySummaryTable';

const KPIGrid: React.FC = () => {
  const { getGridCols } = useResponsive();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { 
    currentAccountBook, 
    isLoading,
    isInitialized,
    dashboardData,
    setSelectedYear
  } = useAccountBookStore();

  const [isChangingYear, setIsChangingYear] = React.useState(false);
  
  // 현재 월 기준으로 데이터 가져오기 — 매 렌더마다 새 Date 생성 시
  // useWorkScheduleData 리스너 및 하위 useMemo 가 무효화되므로 1회만 생성한다.
  const currentMonth = useMemo(() => new Date(), []);
  const { employees, loading: employeesLoading } = useEmployeeData();
  const { schedules, loading: schedulesLoading } = useWorkScheduleData(currentMonth);
  const { monthlyPayrollData, dailyWorkerNotices, monthlyInfoList } = useEmployeePayroll(
    employees,
    schedules,
    currentMonth,
  );
  
  const employeeDataLoading = employeesLoading || schedulesLoading;

  // 년도 변경 함수
  const changeYear = (direction: 'prev' | 'next') => {
    if (isChangingYear) return;
    
    setIsChangingYear(true);
    const currentYear = dashboardData.selectedYear || new Date().getFullYear();
    const newYear = direction === 'prev' ? currentYear - 1 : currentYear + 1;
    
    setSelectedYear(newYear);
    
    setTimeout(() => {
      setIsChangingYear(false);
    }, 500);
  };

  // 연도 변경 시 자동으로 데이터 로딩됨 (setSelectedYear에서 fetchDashboardData 호출)
  // 별도의 fetchDashboardData 호출 불필요
  
  // 실제 Dashboard 데이터 사용
  const kpiData = dashboardData.kpiData;
  const previousYearKpiData = dashboardData.previousYearKpiData;
  
  // 전년도 대비 성장률 계산 함수
  const calculateGrowthRate = (current: number, previous: number): number => {
    if (previous === 0) {
      return current > 0 ? 100 : 0; // 전년도가 0이면 현재가 있으면 100%, 없으면 0%
    }
    return Math.round(((current - previous) / previous) * 100);
  };
  
  const employeeData = useMemo(() => {
    const activeEmployees = employees.filter((emp) => !emp.isRetired);

    if (!activeEmployees.length) {
      return {
        totalEmployees: 0,
        activeEmployees: 0,
        hourlyEmployees: 0,
        monthlyEmployees: 0,
        dailyWorkers: 0,
        businessServices: 0,
        totalMP: 0,
        totalWorkHours: 0,
        totalPayroll: 0,
        totalNetPayroll: 0,
        workHoursChange: 0,
        payrollChange: 0,
      };
    }

    const monthStart = getStartOfMonth(currentMonth);
    const monthEnd = getEndOfMonth(currentMonth);
    const schedulesInMonth = schedules.filter((schedule) => {
      const scheduleDate = new Date(schedule.date);
      if (isNaN(scheduleDate.getTime())) return false;
      return scheduleDate >= monthStart && scheduleDate <= monthEnd;
    });

    const hourlyEmployees = activeEmployees.filter((e) => resolvePayType(e.payType, e.isHourly) === 'hourly').length;
    const monthlyEmployees = activeEmployees.filter((e) => resolvePayType(e.payType, e.isHourly) === 'monthly').length;
    const dailyWorkers = activeEmployees.filter((e) => isDailyWorkerPay(e)).length;
    const businessServices = activeEmployees.filter((e) => isBusinessServicePay(e)).length;
    const totalMP = activeEmployees.reduce((sum, emp) => sum + (emp.mp || 0), 0);

    let totalWorkHours = 0;
    activeEmployees.forEach((employee) => {
      if (isRegularEmployee(employee) && employee.isHourly) {
        const employeeSchedules = schedulesInMonth.filter((s) => s.employee_id === employee.id);
        totalWorkHours += employeeSchedules.length > 0
          ? monthlyPayrollData.find((d) => d.employee.id === employee.id)?.totalWorkHours ?? 0
          : 0;
      }
    });

    const activePayroll = monthlyPayrollData.filter((d) => !d.employee.isRetired);
    const totalPayroll = activePayroll.reduce((sum, d) => sum + d.totalSalary, 0);
    const totalNetPayroll = activePayroll.reduce((sum, d) => sum + d.netSalary, 0);

    return {
      totalEmployees: activeEmployees.length,
      activeEmployees: activeEmployees.length,
      hourlyEmployees,
      monthlyEmployees,
      dailyWorkers,
      businessServices,
      totalMP,
      totalWorkHours: Math.round(totalWorkHours),
      totalPayroll: Math.round(totalPayroll),
      totalNetPayroll: Math.round(totalNetPayroll),
      workHoursChange: 0,
      payrollChange: 0,
    };
  }, [employees, schedules, currentMonth, monthlyPayrollData]);

  // 디버깅: KPI 데이터 확인
  useEffect(() => {
  }, [kpiData, dashboardData.selectedYear, isLoading, isInitialized, currentAccountBook, employeeData]);


  // KPI 데이터 구성 (실제 데이터 사용)
  const kpiCards: Array<{
    title: string;
    value: number | string;
    subtitle: string;
    change: number;
    changeLabel: string;
    type: 'income' | 'expense' | 'profit' | 'neutral';
    trend?: number[];
    emphasis?: boolean;
  }> = [
    {
      title: '총 매출',
      value: kpiData.totalIncome,
      subtitle: `${dashboardData.selectedYear}년 총 매출`,
      change: calculateGrowthRate(kpiData.totalIncome, previousYearKpiData.totalIncome),
      changeLabel: '전년 대비',
      type: 'income' as const,
    },
    {
      title: '총 지출',
      value: kpiData.totalExpense,
      subtitle: `${dashboardData.selectedYear}년 총 지출`,
      change: calculateGrowthRate(kpiData.totalExpense, previousYearKpiData.totalExpense),
      changeLabel: '전년 대비',
      type: 'expense' as const,
    },
    {
      title: '영업이익',
      value: kpiData.netIncome,
      subtitle: '매출 - 지출',
      change: calculateGrowthRate(kpiData.netIncome, previousYearKpiData.netIncome),
      changeLabel: '전년 대비',
      type: 'profit' as const,
      emphasis: true, // 가장 중요한 지표 — 솔리드 그린 히어로 카드
    },
    {
      title: '거래 건수',
      value: kpiData.recordCount,
      subtitle: `${dashboardData.selectedYear}년 총 거래`,
      change: calculateGrowthRate(kpiData.recordCount, previousYearKpiData.recordCount),
      changeLabel: '전년 대비',
      type: 'neutral' as const,
    },
    // 인사관리 카드들
    {
      title: '총 직원 수',
      value: employeeData.totalEmployees, // 퇴직자 제외한 활성 직원 수
      subtitle: [
        `시급 ${employeeData.hourlyEmployees}`,
        `월급 ${employeeData.monthlyEmployees}`,
        employeeData.dailyWorkers > 0 ? `일당 ${employeeData.dailyWorkers}` : null,
        employeeData.businessServices > 0 ? `용역 ${employeeData.businessServices}` : null,
      ].filter(Boolean).join(' · ') + '명',
      change: 0, // 직원 수는 전월 대비로 계산하지 않음
      changeLabel: '',
      type: 'neutral' as const,
    },
    {
      title: '총 Manpower',
      value: employeeData.totalMP,
      subtitle: '전체 인력 규모',
      change: 0,
      changeLabel: '',
      type: 'neutral' as const,
    },
    {
      title: '이번 달 근무시간',
      value: `${employeeData.totalWorkHours}시간`,
      subtitle: '전체 직원 합계',
      change: Math.round(employeeData.workHoursChange),
      changeLabel: '전월 대비',
      type: 'neutral' as const,
    },
    {
      title: '이번 달 급여',
      value: employeeData.totalNetPayroll.toLocaleString(),
      subtitle: (() => {
        const dailyDeduction = monthlyInfoList
          .filter((i) => isDailyWorkerPay(i.employee) && !i.employee.isRetired)
          .reduce((sum, i) => sum + i.totalEmployeeInsurance, 0);
        const base = `총지급 ${employeeData.totalPayroll.toLocaleString()}원`;
        return dailyDeduction > 0
          ? `${base} · 실수령(고용보험 근로자 ${dailyDeduction.toLocaleString()}원 공제)`
          : base;
      })(),
      change: Math.round(employeeData.payrollChange),
      changeLabel: '전월 대비',
      type: 'neutral' as const,
    },
  ];


  const gridCols = getGridCols();

  // 캐시된 데이터가 있으면 즉시 렌더 — 풀 로딩 문구는 보여줄 게 없을 때만
  const hasCachedKpi =
    !!currentAccountBook &&
    (dashboardData.monthlyData.length > 0 || dashboardData.kpiData.recordCount > 0);

  // 초기화가 완료되지 않았거나 로딩 중인 경우
  if ((!isInitialized || isLoading || !currentAccountBook) && !hasCachedKpi) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '30vh',
        gap: 2
      }}>
        <Typography variant="h6" color="text.secondary">
          {!isInitialized ? '데이터를 초기화하는 중...' : 
           !currentAccountBook ? '가계부 정보를 불러오는 중...' : 
           'KPI 데이터를 불러오는 중...'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* 섹션 헤더 — 디자인 시스템 패턴 (eyebrow + display heading + subtitle) */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5
      }}>
        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--fg-tertiary)',
              letterSpacing: '0.031em',
              textTransform: 'uppercase',
              mb: 0.5,
            }}
          >
            재무 현황
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: '-0.019em',
              color: 'var(--fg-strong)',
              lineHeight: 1.2,
              m: 0,
            }}
          >
            {dashboardData.selectedYear}년 영업 지표
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 15,
              color: 'var(--fg-tertiary)',
              letterSpacing: '0.014em',
            }}
          >
            매출·지출·이익 + 인사·근무 지표
          </Typography>
        </Box>
        
        {/* 컴팩트한 년도 선택기 — 필 형태 */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'var(--bg-elev-2)',
          borderRadius: 'var(--r-pill)',
          px: 1.5,
          py: 0.5,
          border: '1px solid var(--border-subtle)',
        }}>
          <IconButton
            aria-label="이전 연도"
            size="small"
            onClick={() => changeYear('prev')}
            disabled={isChangingYear}
            sx={{
              fontSize: 22,
              lineHeight: 1,
              color: isChangingYear ? 'var(--fg-disabled)' : 'var(--primary)',
              '&:hover': {
                backgroundColor: isChangingYear ? 'transparent' : 'var(--primary-soft)',
              },
            }}
          >
            {GLYPHS.prev}
          </IconButton>

          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            sx={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              color: 'var(--fg-strong)',
              minWidth: isMobile ? '60px' : '80px',
              textAlign: 'center',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {dashboardData.selectedYear}년
          </Typography>

          <IconButton
            aria-label="다음 연도"
            size="small"
            onClick={() => changeYear('next')}
            disabled={isChangingYear}
            sx={{
              fontSize: 22,
              lineHeight: 1,
              color: isChangingYear ? 'var(--fg-disabled)' : 'var(--primary)',
              '&:hover': {
                backgroundColor: isChangingYear ? 'transparent' : 'var(--primary-soft)',
              },
            }}
          >
            {GLYPHS.next}
          </IconButton>
        </Box>
      </Box>

              {/* 핵심 재무 KPI — 최우선 지표를 최상단에 (F-패턴 스캔 순서).
                  카드 안 ▲▼ 변화율이 전년 대비를 이미 보여주므로 별도 성장률 요약 박스는 두지 않는다. */}
              <Grid container spacing={3}>
                {kpiCards.slice(0, 4).map((kpi) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={kpi.title}
                  >
                    <StatTile
                      size="md"
                      label={kpi.title}
                      value={typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
                      subtitle={kpi.subtitle}
                      change={kpi.change}
                      changeLabel={kpi.changeLabel}
                      tone={kpi.type}
                      autoFit
                    />
                  </Grid>
                ))}
              </Grid>

              {/* 인사·근무 보조 지표 — 재무 KPI와 시각적 계층 분리 */}
              <Box sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--fg-tertiary)',
                    letterSpacing: '0.031em',
                    textTransform: 'uppercase',
                    flexShrink: 0,
                  }}
                >
                  인사 · 근무
                </Typography>
                <Box sx={{ flex: 1, height: '1px', backgroundColor: 'var(--border-subtle)' }} />
              </Box>
              <Grid container spacing={3}>
                {kpiCards.slice(4).map((kpi) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={kpi.title}
                  >
                    <StatTile
                      size="sm"
                      label={kpi.title}
                      value={typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
                      subtitle={kpi.subtitle}
                      change={kpi.change}
                      changeLabel={kpi.changeLabel}
                      tone={kpi.type}
                      loading={employeeDataLoading}
                    />
                  </Grid>
                ))}
              </Grid>

              <DailyWorkerInsuranceAdvisorCard notices={dailyWorkerNotices} />

              {/* 월별 손익 현황 표 */}
              {dashboardData.monthlyData && dashboardData.monthlyData.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <MonthlySummaryTable 
                    monthlyData={dashboardData.monthlyData}
                    selectedYear={dashboardData.selectedYear}
                  />
                </Box>
              )}

              {/* 추가 정보 */}
              {kpiData.lastUpdated && (
                <Box sx={{ mt: 3, p: 2, backgroundColor: 'var(--bg-elev-2)', borderRadius: 'var(--r-12)', border: '1px solid var(--border-subtle)' }}>
                  <Typography variant="body2" color="text.secondary">
                    마지막 업데이트: {kpiData.lastUpdated.toLocaleDateString('ko-KR')}
                  </Typography>
                </Box>
              )}
    </Box>
  );
};

export default KPIGrid;
